import { useEffect } from "react";

import type { Campaign } from "../../api/generated";
import type { MarketingBannerRow } from "../types";
import type {
  MarketingBannerAsset,
  MarketingBannerConfigurationActions,
  MarketingBannerConfigurationTranslations,
  MarketingBannerFormat,
  MarketingBannerRebateProgramOption,
  MarketingBannerSize,
} from "./types";

import { Button as ButtonV2 } from "../../Button";
import { CampaignPicker } from "../../Campaign";
import { usePendingCampaign } from "../../Campaign/hooks/usePendingCampaign";
import { FieldErrorV2 } from "../../FieldErrorV2";
import { useIsMobile } from "../../hooks/useIsMobile";
import { CheckPaddedIcon, CrossIcon, InfoIcon } from "../../Icons";
import { InputV2 } from "../../InputV2";
import { SegmentControl } from "../../SegmentControl";
import { SelectV3, type SelectV3Option } from "../../SelectV3";
import { showSuccessToast } from "../../Toast";
import { Typo } from "../../Typo";
import {
  BannerPreviewPlaceholder,
  BannerPreviewPlaceholderMobile,
} from "./BannerPreviewPlaceholder";
import { MarketingBannerConfigurationModalShell } from "./MarketingBannerConfigurationModalShell";
import { MarketingBannerConfigurationModalSkeleton } from "./MarketingBannerConfigurationModalSkeleton";
import { MarketingBannerErrorContent } from "./MarketingBannerErrorContent";
import { MarketingBannerNoRebateProgramContent } from "./MarketingBannerNoRebateProgramContent";
import { SizeSelectionCard } from "./SizeSelectionCard";
import {
  sizeHasAssetForLanguage,
  useMarketingBannerConfigurationData,
} from "./useMarketingBannerConfigurationData";

const MODAL_MAX_WIDTH = 968;
const DESKTOP_PREVIEW_DISPLAY_AREA_WIDTH = MODAL_MAX_WIDTH / 2;
const NAME_MAX_LENGTH = 60;

type MarketingBannerConfigurationModalProps = {
  isOpen: boolean;
  onClose: (isRefresh?: boolean) => void;
  onRefresh?: () => void;
  actions: MarketingBannerConfigurationActions;
  translations: MarketingBannerConfigurationTranslations;
  /** Resolves a language code to a display label (was a portal i18n call). */
  getLanguageLabelFromCode: (code: string) => string;
  errorMessage?: string;
  refreshLabel?: string;
  title?: string;
  tags: string[];
  banner?: MarketingBannerRow;
  offerId?: number;
  assets?: MarketingBannerAsset[];
  onViewDuplicate?: (bannerId?: string) => void;
  /** Feature flag: whether the campaign field should be shown at all. */
  showCampaign?: boolean;
  /** Campaign list for selection. */
  campaigns?: Campaign[];
  /** Loading state for campaigns. */
  isLoadingCampaigns?: boolean;
  /** Handler to create a new campaign. */
  onCreateCampaign?: (name: string) => Promise<Campaign>;
  /** Error creating campaign. */
  createCampaignError?: string;
  /** Is campaign being created. */
  isCreatingCampaign?: boolean;
  /** Whether the campaign list failed to load. */
  hasCampaignsError?: boolean;
  /** Retries loading the campaign list. */
  onRetryCampaigns?: () => void;
  /** Fired for each newly-selected rebate-program option (row C1). */
  onRebateProgramChange?: (option: string) => void;
  onCampaignChange?: (option: string) => void;
  /** Fired when the format (Type) tile changes (row C2). */
  onFormatChange?: (value: string) => void;
  /** Fired when a size tile is clicked (row C3). */
  onSizeChange?: (value: string) => void;
  /** Fired for each newly-selected language option (row C4). */
  onLanguageChange?: (option: string) => void;
  /** Fired on every keystroke in the Name field (row C5). */
  onNameChange?: (value: string) => void;
  /** Fired when Create banner is clicked, before the async create call (row C6). */
  onSubmitClick?: (selection: {
    rebateProgram: string;
    type: string;
    size: string;
    language: string;
    name: string;
  }) => void;
};

export const MarketingBannerConfigurationModal = ({
  isOpen,
  title,
  tags,
  banner,
  onClose,
  onRefresh,
  actions,
  translations,
  getLanguageLabelFromCode,
  errorMessage,
  refreshLabel,
  offerId,
  assets,
  onViewDuplicate,
  showCampaign,
  campaigns,
  isLoadingCampaigns,
  onCreateCampaign,
  createCampaignError,
  isCreatingCampaign,
  hasCampaignsError,
  onRetryCampaigns,
  onRebateProgramChange,
  onCampaignChange,
  onFormatChange,
  onSizeChange,
  onLanguageChange,
  onNameChange,
  onSubmitClick,
}: MarketingBannerConfigurationModalProps) => {
  const isMobile = useIsMobile();

  const modalTitle = title ?? translations.title;
  const modalErrorMessage = errorMessage ?? translations.errorMessage;
  const modalRefreshLabel = refreshLabel ?? translations.refreshLabel;

  const {
    values,
    setFormat,
    setSize,
    setLanguage,
    setRebateProgram,
    setCampaign,
    setName,
    reset,
    canSubmit,
    isGettingOfferIds,
    rebateProgramOptions,
    isError,
    retry,
    onCreate,
    isCreating,
  } = useMarketingBannerConfigurationData({
    isOpen,
    title,
    actions,
    rebateAccountLabel: translations.rebateAccountLabel,
    initialValues: {
      format: tags.some((t) => t.toLowerCase() === "html")
        ? "html"
        : tags.some((t) => t.toLowerCase() === "image")
          ? "image"
          : "html",
      banner,
      offerId,
      assets,
    },
    getLanguageLabelFromCode,
  });

  const isSubmitting = isCreating || !!isCreatingCampaign;

  const {
    format,
    size,
    language,
    rebateProgram,
    campaign,
    name,
    status,
    banner: selectedBanner,
    bannerOptions,
    sizes: sizesOptions,
    languageOptions,
  } = values;

  const pendingCampaign = usePendingCampaign({
    campaigns: campaigns ?? [],
    selected: campaign,
    onCreateCampaign,
    createError: createCampaignError,
  });

  const previewBanner =
    size && language
      ? bannerOptions.find(
          (b) =>
            b.width === size.width &&
            b.height === size.height &&
            b.language === language.value,
        )
      : undefined;

  // Only disable sizes while a language is chosen but no size is yet selected,
  // to guide the language-first flow. Once a size is picked, every size stays
  // clickable - switching to a size that lacks the current language selects the
  // first available language for that size, avoiding a dead end where the user
  // can't move to another size.
  const disabledSizeLabels = new Set(
    language && !size
      ? sizesOptions
          .filter(
            (sizeOption) =>
              !sizeHasAssetForLanguage(
                assets,
                format,
                sizeOption,
                language.value,
              ),
          )
          .map((sizeOption) => sizeOption.label)
      : [],
  );

  useEffect(() => {
    if (isOpen) {
      pendingCampaign.reset();
      reset({
        title,
        format: tags.some((t) => t.toLowerCase() === "html")
          ? "html"
          : tags.some((t) => t.toLowerCase() === "image")
            ? "image"
            : "html",
        rebateProgram: rebateProgramOptions[0],
        offerId,
        assets,
        banner,
      });
      return;
    }

    // Only reset on the open transition - initial* changes should not blow away in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  // A failed campaign list blocks the modal the same way a failed offer-ids load does.
  const isBlockedByLoadError = isError || (showCampaign && hasCampaignsError);

  const handleRefresh = () => {
    if (isError) {
      if (onRefresh) onRefresh();
      else retry();
    }
    if (showCampaign && hasCampaignsError) {
      onRetryCampaigns?.();
    }
  };

  // "View banner" on the duplicate warning: always hand off to the host so it
  // can leave the library and show the banners list. The existing banner id is
  // passed when available so the host can also deep-link + highlight the row.
  const handleViewDuplicate = () => {
    onViewDuplicate?.(values.duplicateBannerId);
    handleClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !size || !language || !rebateProgram || !selectedBanner) {
      return;
    }
    onSubmitClick?.({
      rebateProgram: rebateProgram.label,
      type: format ?? "",
      size: size.label,
      language: language.label,
      name,
    });

    let resolvedCampaign: Campaign | undefined;
    try {
      resolvedCampaign = showCampaign
        ? await pendingCampaign.resolve(campaign)
        : undefined;
    } catch {
      return;
    }
    if (resolvedCampaign && resolvedCampaign !== campaign) {
      setCampaign(resolvedCampaign);
    }
    const created = await onCreate({
      format,
      size,
      language,
      rebateProgram,
      campaign: resolvedCampaign,
      name,
      offerId,
      assets,
      banner: selectedBanner,
    });
    if (!created) return;
    onClose(true);
    showSuccessToast({ title: translations.successMessage });
  };

  if (isBlockedByLoadError && isOpen) {
    return (
      <MarketingBannerConfigurationModalShell
        isOpen={isOpen}
        onClose={handleClose}
        isMobile={isMobile}
        title={modalTitle}
        maxWidth={MODAL_MAX_WIDTH}
      >
        <div
          className="flex min-h-[320px] items-center justify-center"
          data-testid="marketing-banner-configuration-modal"
        >
          <MarketingBannerErrorContent
            message={modalErrorMessage}
            refreshLabel={modalRefreshLabel}
            onRefresh={handleRefresh}
          />
        </div>
      </MarketingBannerConfigurationModalShell>
    );
  }

  if (isGettingOfferIds || (showCampaign && isLoadingCampaigns)) {
    const loadingFooter = (
      <div
        aria-hidden="true"
        className="h-10 w-28 animate-pulse rounded-lg bg-surface-tonal-default"
      />
    );

    return (
      <MarketingBannerConfigurationModalShell
        isOpen={isOpen}
        onClose={handleClose}
        isMobile={isMobile}
        title={isMobile ? modalTitle : undefined}
        footer={isMobile ? loadingFooter : undefined}
        maxWidth={MODAL_MAX_WIDTH}
        showDesktopHeader={isMobile}
        modalClassName={isMobile ? undefined : "overflow-hidden"}
        contentContainerClassName={isMobile ? undefined : "!px-0 !gap-0"}
        contentOverflow="auto"
      >
        <MarketingBannerConfigurationModalSkeleton
          isMobile={isMobile}
          showCampaign={showCampaign}
        />
      </MarketingBannerConfigurationModalShell>
    );
  }

  if (rebateProgramOptions.length === 0 && isOpen) {
    return (
      <MarketingBannerConfigurationModalShell
        isOpen={isOpen}
        onClose={handleClose}
        isMobile={isMobile}
        title={modalTitle}
        maxWidth={MODAL_MAX_WIDTH}
      >
        <div
          className="flex min-h-[320px] items-center justify-center"
          data-testid="marketing-banner-configuration-modal"
        >
          <MarketingBannerNoRebateProgramContent
            title={translations.noRebateProgramTitle}
            message={translations.noRebateProgramMessage}
          />
        </div>
      </MarketingBannerConfigurationModalShell>
    );
  }

  return (
    <MarketingBannerConfigurationModalShell
      isOpen={isOpen}
      onClose={handleClose}
      isMobile={isMobile}
      title={isMobile ? modalTitle : undefined}
      footer={
        isMobile && (
          <MarketingBannerSubmitButton
            canSubmit={canSubmit}
            onSubmit={handleSubmit}
            submitLabel={translations.submitLabel}
            isMobile={isMobile}
            isLoading={isSubmitting}
          />
        )
      }
      maxWidth={MODAL_MAX_WIDTH}
      showDesktopHeader={isMobile}
      modalClassName={isMobile ? undefined : "overflow-hidden"}
      contentContainerClassName={isMobile ? undefined : "!px-0 !gap-0"}
      contentOverflow="auto"
    >
      <div data-testid="marketing-banner-configuration-modal">
        {isMobile ? (
          <div className="flex flex-col gap-6">
            <MarketingBannerFields
              format={format}
              size={size}
              language={language}
              rebateProgram={rebateProgram}
              campaign={campaign}
              name={name}
              isMobile={isMobile}
              sizes={sizesOptions}
              disabledSizes={disabledSizeLabels}
              formatOptions={tags}
              languageOptions={languageOptions}
              rebateProgramOptions={rebateProgramOptions}
              showCampaign={showCampaign}
              campaigns={pendingCampaign.campaigns}
              isLoadingCampaigns={isLoadingCampaigns}
              onCreateCampaign={
                onCreateCampaign ? pendingCampaign.stage : undefined
              }
              createCampaignError={pendingCampaign.createError}
              isCreatingCampaign={isCreatingCampaign}
              nameMaxLength={NAME_MAX_LENGTH}
              translations={translations}
              setFormat={setFormat}
              setSize={setSize}
              setLanguage={setLanguage}
              setRebateProgram={setRebateProgram}
              setCampaign={setCampaign}
              setName={setName}
              status={status ?? "idle"}
              onViewDuplicate={handleViewDuplicate}
              onFormatChange={onFormatChange}
              onSizeChange={onSizeChange}
              onLanguageChange={onLanguageChange}
              onRebateProgramChange={onRebateProgramChange}
              onCampaignChange={onCampaignChange}
              onNameChange={onNameChange}
            />
            {previewBanner && (
              <BannerPreviewPlaceholderMobile
                banner={previewBanner}
                isLoading={isSubmitting}
              />
            )}
          </div>
        ) : (
          <div className="flex min-h-[560px]">
            <div className="flex flex-1 items-center justify-center bg-surface-secondary p-8">
              {previewBanner && size && (
                <BannerPreviewPlaceholder
                  banner={previewBanner}
                  size={size}
                  maxBoxWidth={DESKTOP_PREVIEW_DISPLAY_AREA_WIDTH}
                  isLoading={isSubmitting}
                />
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-6 p-8">
              <div className="flex flex-col items-end">
                <button type="button" onClick={handleClose} aria-label="Close">
                  <CrossIcon className="h-6 w-6 text-icon-tertiary" />
                </button>
                <Typo element="h2" type="headingXL" className="w-full">
                  {modalTitle}
                </Typo>
              </div>

              <div className="-mx-2 flex flex-1 flex-col px-2 py-1">
                <MarketingBannerFields
                  format={format}
                  size={size}
                  language={language}
                  rebateProgram={rebateProgram}
                  campaign={campaign}
                  name={name}
                  isMobile={isMobile}
                  sizes={sizesOptions}
                  disabledSizes={disabledSizeLabels}
                  formatOptions={tags}
                  languageOptions={languageOptions}
                  rebateProgramOptions={rebateProgramOptions}
                  showCampaign={showCampaign}
                  campaigns={pendingCampaign.campaigns}
                  isLoadingCampaigns={isLoadingCampaigns}
                  onCreateCampaign={
                    onCreateCampaign ? pendingCampaign.stage : undefined
                  }
                  createCampaignError={pendingCampaign.createError}
                  isCreatingCampaign={isCreatingCampaign}
                  nameMaxLength={NAME_MAX_LENGTH}
                  translations={translations}
                  setFormat={setFormat}
                  setSize={setSize}
                  setLanguage={setLanguage}
                  setRebateProgram={setRebateProgram}
                  setCampaign={setCampaign}
                  setName={setName}
                  status={status ?? "idle"}
                  onViewDuplicate={handleViewDuplicate}
                  onFormatChange={onFormatChange}
                  onSizeChange={onSizeChange}
                  onLanguageChange={onLanguageChange}
                  onRebateProgramChange={onRebateProgramChange}
                  onCampaignChange={onCampaignChange}
                  onNameChange={onNameChange}
                />
              </div>

              <div className="flex justify-end">
                <MarketingBannerSubmitButton
                  canSubmit={canSubmit}
                  onSubmit={handleSubmit}
                  submitLabel={translations.submitLabel}
                  isLoading={isSubmitting}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </MarketingBannerConfigurationModalShell>
  );
};

type MarketingBannerSubmitButtonProps = {
  canSubmit: boolean;
  onSubmit: () => void;
  submitLabel: string;
  isMobile?: boolean;
  isLoading?: boolean;
};

const MarketingBannerSubmitButton = ({
  canSubmit,
  onSubmit,
  submitLabel,
  isMobile,
  isLoading,
}: MarketingBannerSubmitButtonProps) => (
  <ButtonV2
    data-testid="marketing-banner-submit"
    isLoading={isLoading}
    disabled={!canSubmit}
    onClick={onSubmit}
    className={isMobile ? "w-full" : undefined}
  >
    {submitLabel}
  </ButtonV2>
);

type MarketingBannerFieldsProps = {
  format: MarketingBannerFormat | undefined;
  size: MarketingBannerSize | undefined;
  language: SelectV3Option | undefined;
  rebateProgram: MarketingBannerRebateProgramOption | undefined;
  campaign?: Campaign | null;
  name: string;
  isMobile: boolean;
  sizes: MarketingBannerSize[];
  disabledSizes: Set<string>;
  formatOptions: string[];
  languageOptions: SelectV3Option[];
  rebateProgramOptions: MarketingBannerRebateProgramOption[];
  showCampaign?: boolean;
  campaigns?: Campaign[];
  isLoadingCampaigns?: boolean;
  onCreateCampaign?: (name: string) => Promise<Campaign>;
  createCampaignError?: string;
  isCreatingCampaign?: boolean;
  nameMaxLength: number;
  translations: MarketingBannerConfigurationTranslations;
  setFormat: (format: MarketingBannerFormat) => void;
  setSize: (size: MarketingBannerSize) => void;
  setLanguage: (language: SelectV3Option) => void;
  setRebateProgram: (rebateProgram: MarketingBannerRebateProgramOption) => void;
  setCampaign?: (campaign: Campaign | undefined) => void;
  setName: (name: string) => void;
  status: "idle" | "error" | "duplicateName" | "duplicateBanner";
  // Click handler for the duplicate warning's "View banner" link.
  onViewDuplicate: () => void;
  onFormatChange?: (value: string) => void;
  onSizeChange?: (value: string) => void;
  onLanguageChange?: (option: string) => void;
  onRebateProgramChange?: (option: string) => void;
  onCampaignChange?: (option: string) => void;
  onNameChange?: (value: string) => void;
};

const MarketingBannerFields = ({
  format,
  size,
  language,
  rebateProgram,
  campaign,
  name,
  isMobile,
  sizes,
  disabledSizes,
  formatOptions,
  languageOptions,
  rebateProgramOptions,
  showCampaign,
  campaigns,
  isLoadingCampaigns,
  onCreateCampaign,
  createCampaignError,
  isCreatingCampaign,
  nameMaxLength,
  translations,
  setFormat,
  setSize,
  setLanguage,
  setRebateProgram,
  setCampaign,
  setName,
  status,
  onViewDuplicate,
  onFormatChange,
  onSizeChange,
  onLanguageChange,
  onRebateProgramChange,
  onCampaignChange,
  onNameChange,
}: MarketingBannerFieldsProps) => {
  return (
    <div className="flex flex-col gap-6">
      {formatOptions.length > 1 && (
        <SegmentControl<MarketingBannerFormat>
          fullWidth
          options={[
            { label: translations.htmlFormatLabel, value: "html" as const },
            { label: translations.imageFormatLabel, value: "image" as const },
          ]}
          value={format ?? "html"}
          onChange={(value) => {
            onFormatChange?.(value);
            setFormat(value);
          }}
          data-testid="marketing-banner-format"
        />
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Typo type="labelSM">{translations.sizeTitle}</Typo>
          <Typo type="bodyXS" textColor="text-text-secondary">
            {translations.sizeDescription}
          </Typo>
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,71px)] justify-between gap-3 pt-1 lg:flex lg:flex-wrap lg:justify-start">
          {sizes.map((sizeOption) => (
            <SizeSelectionCard
              key={sizeOption.label}
              size={sizeOption}
              selected={size?.label === sizeOption.label}
              disabled={disabledSizes.has(sizeOption.label)}
              onClick={() => {
                onSizeChange?.(sizeOption.label);
                setSize(sizeOption);
              }}
            />
          ))}
        </div>
      </div>

      <SelectV3
        isMobile={isMobile}
        label={translations.languageTitle}
        subLabel={translations.languageDescription}
        placeholder={translations.languagePlaceholder}
        options={languageOptions}
        value={language}
        onChange={(option) => {
          onLanguageChange?.(option.label);
          setLanguage(option);
        }}
        enableSearch={false}
        modal
        labelType="labelSM"
      />

      {rebateProgramOptions.length > 1 && (
        <div className="flex flex-col gap-2">
          <SelectV3
            isMobile={isMobile}
            label={translations.rebateProgramTitle}
            subLabel={translations.rebateProgramDescription}
            options={rebateProgramOptions}
            value={rebateProgram}
            onChange={(option) => {
              const selected = rebateProgramOptions.find(
                (rebateProgramOption) =>
                  rebateProgramOption.value === option.value,
              );
              if (selected) {
                onRebateProgramChange?.(selected.label);
                setRebateProgram(selected);
              }
            }}
            enableSearch={false}
            modal
            labelType="labelSM"
            dropdownListClassName="p-4"
            renderValue={(option) => (
              <div className="flex flex-col text-left">
                <Typo
                  element="span"
                  type="bodySM"
                  textColor="text-text-primary"
                >
                  {option.label}
                </Typo>
              </div>
            )}
            renderOptionCell={({ option, isSelected }) => (
              <>
                <div className="flex flex-col text-left">
                  <Typo
                    element="span"
                    type="labelSM"
                    textColor={
                      isSelected ? "text-text-selected" : "text-text-primary"
                    }
                  >
                    {option.label}
                  </Typo>
                  {option.description && (
                    <Typo
                      element="span"
                      type="bodyXS"
                      textColor={
                        isSelected
                          ? "text-text-selected"
                          : "text-text-secondary"
                      }
                    >
                      {option.description}
                    </Typo>
                  )}
                </div>
                <CheckPaddedIcon
                  className={`h-6 w-6 [&_path]:stroke-stroke-selected ${isSelected ? "visible" : "invisible"}`}
                />
              </>
            )}
          />
          {/* TODO: (PART-3623): master/sub IB reminder is disabled until the
               selected HOID's master/sub IB relationship data is available
               (PART-3095). It must never show in this phase. The previous
               `rebateProgram?.value === rebateProgramOptions[0].value` check tied
               visibility to the first rebate program rather than the actual
               relationship, so it always showed for the first program. When the
               data exists, gate on the relationship:

               hasMasterSubRelationship && (
                 <span className="flex flex-row items-center gap-2">
                   <InfoIcon className="[&_path]:stroke-icon-brand-default" />
                   <Typo type="bodyXS" textColor="text-text-secondary">
                     {translations.reminderHOIDMessage}
                   </Typo>
                 </span>
               )
            */}
        </div>
      )}

      {showCampaign && setCampaign && onCreateCampaign && (
        <CampaignPicker
          value={campaign ?? undefined}
          campaigns={campaigns ?? []}
          isLoading={isLoadingCampaigns}
          onSelect={(selection) => {
            onCampaignChange?.(selection ? selection.name : "none");
            setCampaign(selection ?? undefined);
          }}
          onCreate={onCreateCampaign}
          createError={createCampaignError}
          disabled={isCreatingCampaign}
          maxLength={nameMaxLength}
        />
      )}

      <InputV2
        name="marketing-banner-name"
        label={translations.nameTitle}
        subLabel={translations.nameDescription}
        value={name}
        onChange={(value) => {
          onNameChange?.(value);
          setName(value);
        }}
        maxLength={nameMaxLength}
        characterLimit={nameMaxLength}
        labelType="labelSM"
        error={
          status === "duplicateName"
            ? translations.duplicateNameFieldError
            : undefined
        }
      />

      {status === "duplicateBanner" && (
        <FieldWarning
          text={
            showCampaign
              ? translations.duplicateNameMessageWithCampaign
              : translations.duplicateNameMessage
          }
          viewLinkText={translations.duplicateNameViewLinkText}
          handleClose={onViewDuplicate}
        />
      )}

      {status === "error" && (
        <FieldError text={translations.genericErrorMessage} />
      )}
    </div>
  );
};

const FieldError = ({ text }: { text: string }) => (
  <div
    className="rounded-lg bg-background-failed p-3"
    data-testid="create-link-error"
  >
    <FieldErrorV2 text={text} />
  </div>
);

const FieldWarning = ({
  text,
  viewLinkText,
  handleClose,
}: {
  text: string;
  viewLinkText: string;
  handleClose: () => void;
}) => (
  <div
    className="flex items-center justify-between gap-4 rounded-lg bg-background-warning p-4"
    data-testid="create-link-duplicate"
  >
    <div className="flex items-start gap-2">
      <InfoIcon className="h-4 w-4 shrink-0 [&_path]:stroke-text-warning-bold" />
      <Typo type="bodySM" textColor="text-text-warning-bold">
        {text}
      </Typo>
    </div>
    <button type="button" onClick={handleClose} className="shrink-0">
      <Typo
        type="labelXS"
        textColor="text-text-primary"
        className="whitespace-nowrap underline"
      >
        {viewLinkText}
      </Typo>
    </button>
  </div>
);
